// ═══════════════════════════════════════════════════════════════════════════
// @dxf-ai-editor/command-registry — system category descriptors
// ═══════════════════════════════════════════════════════════════════════════
//
// Category: "system" — utility commands that operate on the drawing as a
// whole or on its housekeeping state (purge, rename, properties palette,
// match-properties, change-properties, annotative scale, geometric
// constraints).
//
//   • PURGE         — remove unreferenced block definitions. Dispatches
//                     `file/purge-blocks` (editor.ts:1306). CLI handler
//                     opens PurgeBlocksDialog (App.tsx:1191-1199).
//   • RENAME        — rename a block with cascade across INSERTs.
//                     Dispatches `file/rename-block` (editor.ts:1316).
//                     CLI handler opens RenameBlockDialog
//                     (App.tsx:1200-1207).
//   • ANNOUPDATE    — sync annotative INSERTs to current CANNOSCALE.
//                     Dispatches `geometry/annoupdate` (editor.ts:1280).
//   • OBJECTSCALE   — add / remove an annotation scale context for an
//                     INSERT. Dispatches `geometry/objectscale-add`
//                     (editor.ts:1288) or `geometry/objectscale-remove`
//                     (editor.ts:1294).
//   • MATCHPROP     — copy properties from a source entity to targets.
//                     Dispatches `matchprop/set-source` (editor.ts:248) +
//                     `matchprop/apply` (editor.ts:253).
//   • CHPROP        — change properties on selected entities (modal).
//                     Dispatches `chprop/apply` (editor.ts:241).
//   • PROPERTIES    — open the Properties palette. Dispatches
//                     `properties/apply` (editor.ts:235) when applying
//                     edits from the palette.
//   • CANNOSCALE    — set the current CANNOSCALE (annotation scale).
//                     Dispatches `header/set-cannoscale` (editor.ts:1271).
//   • GCCONSTRAINT  — add a geometric constraint between picked entities.
//                     Internal palette-driven flow inside BEDIT;
//                     dispatches `geometry/add-geometric-constraint`
//                     (editor.ts:871).
//
// Sources of truth: editor.ts:235-253, :1271, :1280-1297, :1306-1319,
// :871; App.tsx:1191-1207 (PURGE / RENAME); draftingHelpers.ts:115-118,
// :575 (GCCONSTRAINT entry); messages.ts:1667-1668, :1691-1692,
// :4175-4176, :4198-4199 (RU+EN labels for PURGE / RENAME / ANNOUPDATE
// / OBJECTSCALE).
//
// COVERAGE_ALLOWLIST coordination — historical migration note:
// originally the literals listed below lived in
// `packages/command-registry/src/coverage-allowlist.ts` (groups 2, 3,
// 5, 6) and were migrated out as descriptors landed. Today the only
// allowlist entry related to this file is `header/set-cannoscale`
// (group 5, PR-8), the no-materialize sibling that has no CLI surface;
// the user-facing CANNOSCALE dispatches `header/set-cannoscale-and-sync`
// per PR-8.
// PR-8 (annotative status-bar / scale-list commands) added five new
// descriptors (ANNORESET / SCALELISTEDIT / ANNOMONITOR / ANNOALLVISIBLE
// / ANNOAUTOSCALE) and switched CANNOSCALE's dispatcher; see the
// `// ─── CANNOSCALE — PR-8 ...` comment block below.
//
// `geometry/add-geometric-constraint` is NOT in the allowlist — it has
// no listing today and the GCCONSTRAINT descriptor here is the first
// owner. status: "implemented" because the palette flow exists
// (BlockAuthoringPalette → onStartGeometricConstraint), even though
// command-line typed-name routing for GCCONSTRAINT is not wired yet.
// ═══════════════════════════════════════════════════════════════════════════

import type { CommandDescriptor } from "../types.js";

const REDUCER_GEOMETRY = "apps/web/src/app/state/geometryReducer.ts" as const;
const REDUCER_PROPERTY = "apps/web/src/app/state/propertyReducer.ts" as const;
const REDUCER_STYLE_TABLES =
  "apps/web/src/app/state/styleTablesReducer.ts" as const;

const PURGE: CommandDescriptor = {
  id: "PURGE",
  category: "system",
  status: "implemented",
  // Backed by the AI PURGE_BLOCKS safe-contour action (file/purge-blocks).
  aiCallable: true,
  aliases: { en: ["PURGE", "PU"], ru: ["ОЧИСТИТЬ"] },
  commandTypeLiterals: ["file/purge-blocks"],
  parameters: [
    {
      name: "blockNames",
      type: "object",
      required: true,
      description: {
        ru: "Массив имён блоков для удаления (минимум 1). Перед удалением reducer повторно прогоняет purgeBlocksMutation чтобы исключить блоки, на которые появились ссылки после открытия диалога.",
        en: "Array of block names to remove (at least 1). Before deletion the reducer re-runs purgeBlocksMutation to exclude blocks that gained references after the dialog opened.",
      },
    },
  ],
  help: {
    ru: {
      short: "Удаляет неиспользуемые определения блоков из чертежа.",
      long: "Команда ОЧИСТИТЬ (PURGE) удаляет из чертежа определения блоков, на которые нет ссылок (нет ни одного INSERT'а ни в одном пространстве). UI открывает диалог PurgeBlocksDialog, который запускает purgeBlocksMutation на стороне клиента для составления списка кандидатов; пользователь подтверждает подмножество, и dispatch'ится команда с подтверждённым blockNames. Reducer выполняет защитный повторный анализ — если за время диалога на блок появилась ссылка (например, через AI или второй вкладкой), он исключается из удаления. Будущая безUIшная форма -PURGE планируется на следующую фазу.",
      examples: ["PURGE", "PU", "ОЧИСТИТЬ"],
      seeAlso: ["RENAME", "BEDIT"],
    },
    en: {
      short: "Removes unreferenced block definitions from the drawing.",
      long: "The PURGE command removes block definitions from the drawing that have no references (no INSERT in any space). The UI opens PurgeBlocksDialog which runs purgeBlocksMutation client-side to populate the candidate list; the user confirms a subset and the command dispatches with the confirmed blockNames. The reducer performs a defensive re-analysis — if a block gained a reference while the dialog was open (e.g. via AI or a second tab), it is excluded from deletion. A future no-UI -PURGE form is scheduled for a later phase.",
      examples: ["PURGE", "PU"],
      seeAlso: ["RENAME", "BEDIT"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.purge.label",
    labelKeyEn: "command.purge.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: "PU",
  },
  reducerLocations: [
    { commandTypeLiteral: "file/purge-blocks", file: REDUCER_GEOMETRY },
  ],
};

const RENAME: CommandDescriptor = {
  id: "RENAME",
  category: "system",
  status: "implemented",
  // Backed by the AI RENAME_BLOCK safe-contour action (file/rename-block).
  aiCallable: true,
  aliases: { en: ["RENAME", "REN"], ru: ["ПЕРЕИМЕНОВАТЬ", "ПЕРЕИМ"] },
  commandTypeLiterals: ["file/rename-block"],
  parameters: [
    {
      name: "oldName",
      type: "string",
      required: true,
      description: {
        ru: "Текущее имя блока. Должно соответствовать существующей записи в blocks{} / blockRecords{}. Регистр имеет значение.",
        en: "Current block name. Must match an existing entry in blocks{} / blockRecords{}. Case-sensitive.",
      },
    },
    {
      name: "newName",
      type: "string",
      required: true,
      description: {
        ru: 'Новое имя блока. Должно быть уникальным; не должно содержать запрещённых символов (* ? \\ / | < > " :) и не должно совпадать с зарезервированными именами (Model, Layout, *MODEL_SPACE и т.д.).',
        en: 'New block name. Must be unique; must not contain forbidden characters (* ? \\ / | < > " :) and must not collide with reserved names (Model, Layout, *MODEL_SPACE, etc.).',
      },
    },
  ],
  help: {
    ru: {
      short: "Переименовывает блок с каскадным обновлением всех INSERT-ссылок.",
      long: "Команда ПЕРЕИМЕНОВАТЬ (RENAME) переименовывает определение блока с автоматическим обновлением всех ссылок: blocks[newName], blockRecords[newName] и каждый INSERT в обоих пространствах + во вложенных block definitions. UI открывает RenameBlockDialog с inline-валидацией (дубликаты, зарезервированные имена, запрещённые символы). При нажатии OK reducer вызывает renameBlockMutation. Команда работает только для пользовательских блоков — попытка переименовать системный блок (Model, *MODEL_SPACE, *PAPER_SPACE, *Uxxx) отклоняется. AutoCAD позволяет также переименовывать слои, типы линий и стили — эти варианты будут добавлены в следующих фазах.",
      examples: ["RENAME", "REN", "ПЕРЕИМЕНОВАТЬ"],
      seeAlso: ["PURGE", "BEDIT"],
    },
    en: {
      short: "Renames a block with cascading INSERT-reference updates.",
      long: "The RENAME command renames a block definition with automatic reference updates: blocks[newName], blockRecords[newName] and every INSERT in both spaces + nested block definitions. The UI opens RenameBlockDialog with inline validation (duplicates, reserved names, forbidden characters). On OK the reducer calls renameBlockMutation. Only user blocks can be renamed — system blocks (Model, *MODEL_SPACE, *PAPER_SPACE, *Uxxx) reject the operation. AutoCAD also lets you rename layers, line types and styles — those variants will be added in later phases.",
      examples: ["RENAME", "REN"],
      seeAlso: ["PURGE", "BEDIT"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.rename.label",
    labelKeyEn: "command.rename.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: "RN",
  },
  reducerLocations: [
    { commandTypeLiteral: "file/rename-block", file: REDUCER_GEOMETRY },
  ],
};

const ANNOUPDATE: CommandDescriptor = {
  id: "ANNOUPDATE",
  category: "system",
  status: "implemented",
  // Annotative-scale refresh/regeneration class (akin to REGEN, ТЗ §10.1
  // REGENALL = no). Not AI-callable.
  aiCallable: false,
  aliases: { en: ["ANNOUPDATE"], ru: ["АННОТОБНОВ", "АННОБНОВ"] },
  commandTypeLiterals: ["geometry/annoupdate"],
  parameters: [],
  help: {
    ru: {
      short: "Синхронизирует все аннотативные INSERT'ы с текущим CANNOSCALE.",
      long: "Команда АННОТОБНОВ (ANNOUPDATE) обходит все аннотативные INSERT'ы во всех пространствах и переписывает их scaleX/scaleY в соответствии с текущим масштабом аннотаций (header.cannoscale). Это нужно когда пользователь изменил CANNOSCALE через UI или import'ом и хочет одномоментно подтянуть отображение всех аннотативных блоков (рамки штампов, символов, текстовых блоков с автомасштабом). Команда не модифицирует определения блоков — только scaleX/scaleY на каждом INSERT'е. Аналог AutoCAD ANNOUPDATE.",
      examples: ["ANNOUPDATE", "АННОТОБНОВ"],
      seeAlso: ["OBJECTSCALE", "CANNOSCALE"],
    },
    en: {
      short: "Synchronises all annotative INSERTs with the current CANNOSCALE.",
      long: "The ANNOUPDATE command walks every annotative INSERT across all spaces and rewrites their scaleX/scaleY according to the current annotation scale (header.cannoscale). Useful when CANNOSCALE was changed via UI or import and the user wants to refresh all annotative blocks at once (title-block frames, symbols, auto-scaling text blocks). The command does NOT modify block definitions — only scaleX/scaleY on each INSERT. Mirrors AutoCAD ANNOUPDATE.",
      examples: ["ANNOUPDATE"],
      seeAlso: ["OBJECTSCALE", "CANNOSCALE"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.annoupdate.label",
    labelKeyEn: "command.annoupdate.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: "AU",
  },
  reducerLocations: [
    { commandTypeLiteral: "geometry/annoupdate", file: REDUCER_GEOMETRY },
  ],
};

const OBJECTSCALE: CommandDescriptor = {
  id: "OBJECTSCALE",
  category: "system",
  status: "implemented",
  // DEFAULT-FALSE (flagged for review): annotative scale-list editing on
  // objects; no dedicated AI safe-contour action, absent from ТЗ §10.1.
  aiCallable: false,
  aliases: {
    en: ["OBJECTSCALE", "-OBJECTSCALE"],
    ru: ["МСШТОБЪЕКТ", "ОБЪМАСШТАБ"],
  },
  commandTypeLiterals: [
    "geometry/objectscale-add",
    "geometry/objectscale-remove",
  ],
  parameters: [
    {
      name: "insertId",
      type: "entityId",
      required: true,
      description: {
        ru: "Идентификатор INSERT, у которого изменяется список аннотационных масштабов.",
        en: "INSERT entity id whose annotation-scales list is modified.",
      },
    },
    {
      name: "scaleName",
      type: "string",
      required: true,
      description: {
        ru: "Имя масштаба аннотации (например, '1:50', '1:100'). Должен существовать в таблице scales чертежа.",
        en: "Annotation scale name (e.g. '1:50', '1:100'). Must exist in the drawing's scales table.",
      },
    },
  ],
  help: {
    ru: {
      short:
        "Добавляет или удаляет контекст аннотационного масштаба для INSERT'а.",
      long: "Команда МСШТОБЪЕКТ (OBJECTSCALE) обновляет список annotationScales выбранного INSERT'а — отдельный список масштабов, при которых данный экземпляр должен быть видим. Если текущий CANNOSCALE есть в этом списке, INSERT отрисовывается; иначе скрывается. Команда имеет два literal'а: geometry/objectscale-add (добавить scaleName в список) и geometry/objectscale-remove (удалить). Используется для тонкой настройки видимости аннотативных блоков на разных видах одного чертежа (например, штамп виден только на масштабе 1:50, мелкая деталировка — только на 1:10). Минусовая форма -OBJECTSCALE даёт command-line диалог; обычная форма открывает Object Scale dialog. Перед запуском нужен выбранный INSERT.",
      examples: ["OBJECTSCALE", "МСШТОБЪЕКТ", "-OBJECTSCALE"],
      seeAlso: ["ANNOUPDATE", "CANNOSCALE"],
    },
    en: {
      short: "Adds or removes an annotation scale context for an INSERT.",
      long: "The OBJECTSCALE command updates the selected INSERT's annotationScales list — a per-instance list of scales at which that INSERT should be visible. If the current CANNOSCALE is in the list the INSERT renders; otherwise it is hidden. The command has two literals: geometry/objectscale-add (add scaleName) and geometry/objectscale-remove (remove). Used for fine-grained visibility of annotative blocks across views of the same drawing (e.g. title block visible only at 1:50, fine detailing only at 1:10). The -OBJECTSCALE minus form gives a command-line dialog; the regular form opens the Object Scale dialog. Requires a selected INSERT.",
      examples: ["OBJECTSCALE", "-OBJECTSCALE"],
      seeAlso: ["ANNOUPDATE", "CANNOSCALE"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.objectscale.label",
    labelKeyEn: "command.objectscale.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: "OS",
  },
  reducerLocations: [
    { commandTypeLiteral: "geometry/objectscale-add", file: REDUCER_GEOMETRY },
    {
      commandTypeLiteral: "geometry/objectscale-remove",
      file: REDUCER_GEOMETRY,
    },
  ],
};

const MATCHPROP: CommandDescriptor = {
  id: "MATCHPROP",
  category: "system",
  status: "implemented",
  // ТЗ §10.1: MATCHPROP AI-callable = yes (property copy source→destinations).
  aiCallable: true,
  aliases: {
    en: ["MATCHPROP", "MA", "PAINTER"],
    ru: ["КОПИРСВ", "КОПИРОВАТЬСВ"],
  },
  commandTypeLiterals: ["matchprop/set-source", "matchprop/apply"],
  parameters: [
    {
      name: "sourceEntityId",
      type: "entityId",
      required: true,
      description: {
        ru: "Идентификатор сущности-донора, чьи свойства копируются. Устанавливается через matchprop/set-source.",
        en: "Entity id of the source whose properties are copied. Set via matchprop/set-source.",
      },
    },
    {
      name: "targetEntityIds",
      type: "entityIdList",
      required: true,
      description: {
        ru: "Идентификаторы сущностей-получателей. Каждый dispatch matchprop/apply применяет копируемые свойства к одной партии целей.",
        en: "Target entity ids. Each matchprop/apply dispatch applies the copied properties to one batch of targets.",
      },
    },
    {
      name: "mode",
      type: "enum",
      enumValues: ["basic", "special", "all"],
      required: false,
      description: {
        ru: "Режим копирования свойств (как набор в диалоге «Параметры копирования свойств» AutoCAD): 'basic' — только базовые свойства (цвет, слой, тип линий, масштаб типа линий, вес линий, прозрачность, толщина, стиль печати); 'special' — только специальные/типозависимые свойства (размерный стиль, текстовый стиль, штриховка, полилиния, видовой экран, таблица, материал, мультивыноска); 'all' — и базовые, и специальные (по умолчанию).",
        en: "Property-copy mode (the property set in AutoCAD's Property Settings dialog): 'basic' — basic properties only (color, layer, linetype, linetype scale, lineweight, transparency, thickness, plot style); 'special' — special/type-specific properties only (dimension style, text style, hatch, polyline, viewport, table, material, multileader); 'all' — both basic and special (default).",
      },
    },
  ],
  help: {
    ru: {
      short:
        "Копирует свойства от одной сущности к одной или нескольким целевым (Property Painter).",
      long: "Команда КОПИРСВ (MATCHPROP, также PAINTER) реализует двухэтапный flow 'копировать свойства': сначала диспатчится matchprop/set-source с id сущности-донора (пользователь кликает по источнику), затем повторные matchprop/apply с разными targetEntityIds применяют скопированные свойства к каждой целевой сущности. Цикл активен до Esc или смены команды. Копируются: color, layer, linetype, lineweight, plot style, text style (для текстов), dimension style (для размеров), hatch pattern (для штриховок), плюс прочие type-specific свойства. Параметр mode выбирает набор свойств как в диалоге «Параметры копирования свойств»: 'basic' (базовые), 'special' (специальные/типозависимые) или 'all' (оба набора, по умолчанию). Аналогично AutoCAD MATCHPROP / 'формат по образцу'.",
      examples: ["MATCHPROP", "MA", "КОПИРСВ"],
      seeAlso: ["CHPROP", "PROPERTIES"],
    },
    en: {
      short:
        "Copies properties from one entity to one or more targets (Property Painter).",
      long: "The MATCHPROP command (also PAINTER) implements a two-step 'copy properties' flow: first matchprop/set-source dispatches with the source entity id (the user clicks a source), then repeated matchprop/apply calls with different targetEntityIds apply the copied properties to each target. The loop stays active until Esc or a command change. Copied properties: color, layer, linetype, lineweight, plot style, text style (for text), dimension style (for dimensions), hatch pattern (for hatches), plus other type-specific properties. The mode parameter selects the property set like AutoCAD's Property Settings dialog: 'basic' (basic), 'special' (special/type-specific), or 'all' (both sets, default). Mirrors AutoCAD MATCHPROP / 'format painter'.",
      examples: ["MATCHPROP", "MA"],
      seeAlso: ["CHPROP", "PROPERTIES"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.matchprop.label",
    labelKeyEn: "command.matchprop.label",
    ribbonTab: null,
    ribbonSection: "ribbon.section.properties",
    glyph: "MP",
  },
  reducerLocations: [
    { commandTypeLiteral: "matchprop/set-source", file: REDUCER_PROPERTY },
    { commandTypeLiteral: "matchprop/apply", file: REDUCER_PROPERTY },
  ],
};

const CHPROP: CommandDescriptor = {
  id: "CHPROP",
  category: "system",
  status: "implemented",
  // ТЗ §10.1: CHPROP AI-callable = no (property-change channel reserved for
  // user/UI; status matrix dims.ai = "na").
  aiCallable: false,
  aliases: { en: ["CHPROP", "-CH"], ru: ["ИЗМСВ", "ИЗМСВОЙ"] },
  commandTypeLiterals: ["chprop/apply"],
  parameters: [
    {
      name: "entityIds",
      type: "entityIdList",
      required: true,
      description: {
        ru: "Идентификаторы сущностей, чьи свойства изменяются.",
        en: "Entity ids whose properties are modified.",
      },
    },
    {
      name: "updates",
      type: "object",
      required: true,
      description: {
        ru: "Объект с полями свойств: { color?, layer?, linetype?, lineweight?, plotStyle?, ltscale?, thickness? }. Только указанные поля применяются; отсутствующие остаются прежними.",
        en: "Property update object: { color?, layer?, linetype?, lineweight?, plotStyle?, ltscale?, thickness? }. Only specified fields apply; missing ones stay unchanged.",
      },
    },
  ],
  help: {
    ru: {
      short:
        "Изменяет общие свойства выбранных сущностей (color, layer, linetype, lineweight, ...).",
      long: "Команда ИЗМСВ (CHPROP) — модальный аналог Properties palette: применяет batch-обновление общих свойств к выбранному набору сущностей. Изменяемые поля: color, layer, linetype, lineweight, plot style, ltscale (множитель типа линии), thickness. Type-specific свойства (radius для CIRCLE, height для TEXT, ...) НЕ редактируются через CHPROP — для этого используется PROPERTIES. CHPROP полезен в скриптах и для быстрых массовых правок без открытия palette. Аналог AutoCAD CHPROP / -CH.",
      examples: ["CHPROP", "ИЗМСВ"],
      seeAlso: ["PROPERTIES", "MATCHPROP"],
    },
    en: {
      short:
        "Changes general properties of the selected entities (color, layer, linetype, lineweight, ...).",
      long: "The CHPROP command is the modal counterpart of the Properties palette: applies a batch update of general properties to the selected entity set. Editable fields: color, layer, linetype, lineweight, plot style, ltscale (linetype scale), thickness. Type-specific properties (CIRCLE radius, TEXT height, ...) are NOT editable via CHPROP — use PROPERTIES for those. CHPROP is useful in scripts and for quick mass edits without opening the palette. Mirrors AutoCAD CHPROP / -CH.",
      examples: ["CHPROP"],
      seeAlso: ["PROPERTIES", "MATCHPROP"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.chprop.label",
    labelKeyEn: "command.chprop.label",
    ribbonTab: null,
    ribbonSection: "ribbon.section.properties",
    glyph: "CP",
  },
  reducerLocations: [
    { commandTypeLiteral: "chprop/apply", file: REDUCER_PROPERTY },
  ],
};

const PROPERTIES: CommandDescriptor = {
  id: "PROPERTIES",
  category: "system",
  status: "implemented",
  // ТЗ §10.1: PROPERTIES AI-callable = no (palette is a UI surface; status
  // matrix dims.ai = "na").
  aiCallable: false,
  aliases: {
    en: ["PROPERTIES", "PR", "MO", "PROPS", "CH", "DDMODIFY"],
    ru: ["СВОЙСТВА", "ОКНОСВ"],
  },
  commandTypeLiterals: ["properties/apply"],
  parameters: [
    {
      name: "entityIds",
      type: "entityIdList",
      required: true,
      description: {
        ru: "Идентификаторы сущностей, чьи свойства редактируются (минимум 1; пустой выбор открывает диалог настроек чертежа).",
        en: "Entity ids whose properties are edited (at least 1; empty selection opens the drawing-settings dialog).",
      },
    },
    {
      name: "updates",
      type: "object",
      required: true,
      description: {
        ru: "Полный набор обновлений включая type-specific поля (radius, height, content, и т.д.). Структура зависит от типа сущности; reducer валидирует против match-shapes.",
        en: "Full update set including type-specific fields (radius, height, content, ...). Structure depends on entity type; reducer validates against match-shapes.",
      },
    },
  ],
  help: {
    ru: {
      short:
        "Открывает Properties palette для редактирования общих и type-specific свойств выбранных сущностей.",
      long: "Команда СВОЙСТВА (PROPERTIES, также PR, MO, PROPS, CH, DDMODIFY) открывает (или фокусирует) Properties palette — основной интерфейс редактирования свойств сущностей. В отличие от CHPROP (только общие свойства), PROPERTIES редактирует ВСЕ свойства включая type-specific: radius/diameter для CIRCLE, height/content/style для TEXT, vertices/closed для PLINE, и т.д. Дисcпатчится properties/apply при каждом коммите изменений в палитре. Палитра поддерживает множественный выбор: общие свойства редактируются для всех; type-specific доступны только когда все выбранные сущности — одного типа. Открыта-закрыта через `commandline/submit text='PROPERTIES'` (см. RibbonConfig.ts).",
      examples: ["PROPERTIES", "PR", "MO", "СВОЙСТВА"],
      seeAlso: ["CHPROP", "MATCHPROP"],
    },
    en: {
      short:
        "Opens the Properties palette for editing general and type-specific properties of the selected entities.",
      long: "The PROPERTIES command (also PR, MO, PROPS, CH, DDMODIFY) opens (or focuses) the Properties palette — the main entity-property editing interface. Unlike CHPROP (general properties only), PROPERTIES edits ALL properties including type-specific ones: radius/diameter for CIRCLE, height/content/style for TEXT, vertices/closed for PLINE, etc. Dispatches properties/apply on each commit from the palette. Multi-selection is supported: general properties edit for all selected; type-specific show only when every selected entity is of the same type. Opened/closed via `commandline/submit text='PROPERTIES'` (see RibbonConfig.ts).",
      examples: ["PROPERTIES", "PR", "MO"],
      seeAlso: ["CHPROP", "MATCHPROP"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.properties.label",
    labelKeyEn: "command.properties.label",
    ribbonTab: null,
    ribbonSection: "ribbon.section.properties",
    glyph: "PR",
  },
  reducerLocations: [
    { commandTypeLiteral: "properties/apply", file: REDUCER_PROPERTY },
  ],
};

// ─── CANNOSCALE — PR-8: dispatcher switched to header/set-cannoscale-and-sync ─
// PR-8 changed CANNOSCALE's user-facing dispatcher from `header/set-cannoscale`
// to `header/set-cannoscale-and-sync` (autoMaterialize=true) so that
// command-line `CANNOSCALE 1:50` triggers the same ANNOAUTOSCALE materialization
// pass as the status-bar dropdown (AnnotationStatusBar.tsx). The plain
// `header/set-cannoscale` action remains available in editor.ts for
// programmatic callers that explicitly want the no-materialize variant
// (future viewport-specific overrides, internal tests). The reducer treats
// the sync variant as a single undo step per AutoCAD parity.
const CANNOSCALE: CommandDescriptor = {
  id: "CANNOSCALE",
  category: "system",
  status: "implemented",
  // Backed by the AI SET_CANNOSCALE safe-contour action
  // (header/set-cannoscale). AI-callable.
  aiCallable: true,
  aliases: { en: ["CANNOSCALE"], ru: ["ТЕКМСШТАБА", "ТЕКМАСШТАБ"] },
  commandTypeLiterals: ["header/set-cannoscale-and-sync"],
  parameters: [
    {
      name: "cannoscaleName",
      type: "string",
      required: true,
      description: {
        ru: "Имя текущего масштаба аннотаций (например, '1:1', '1:50', '1:100'). Должно существовать в таблице scales чертежа.",
        en: "Current annotation-scale name (e.g. '1:1', '1:50', '1:100'). Must exist in the drawing's scales table.",
      },
    },
    {
      name: "autoMaterialize",
      type: "boolean",
      required: false,
      defaultValue: "true",
      description: {
        ru: "Нужно ли при смене масштаба автоматически добавить контекст нового масштаба ко всем annotative-сущностям (ANNOAUTOSCALE-материализация). По умолчанию true; если header.annoAutoScale ≤ 0, материализация всё равно пропускается.",
        en: "Whether to automatically add a context for the new scale to every annotative entity (ANNOAUTOSCALE materialization). Defaults to true; if header.annoAutoScale ≤ 0 the materialization is skipped regardless.",
      },
    },
  ],
  help: {
    ru: {
      short: "Устанавливает текущий масштаб аннотаций (CANNOSCALE).",
      long: "Команда ТЕКМСШТАБА (CANNOSCALE) устанавливает значение системной переменной CANNOSCALE — текущего масштаба аннотаций для документа. Этот масштаб определяет: (1) какие annotative INSERT'ы видны (только те, у которых текущий scaleName есть в их annotationScales списке — см. OBJECTSCALE); (2) на какой scale factor пересчитывается scaleX/scaleY annotative INSERT'ов при следующем ANNOUPDATE. Также отображается в строке состояния AutoCAD. Команда диспатчит header/set-cannoscale-and-sync (PR-8): помимо обновления header.cannoscale, при включённом ANNOAUTOSCALE дополнительно материализует новый масштаб для всех annotative-сущностей в одну транзакцию (одна undo-ступень). Поведение полностью эквивалентно выбору масштаба через AnnotationStatusBar dropdown.",
      examples: ["CANNOSCALE 1:50", "ТЕКМСШТАБА 1:100"],
      seeAlso: ["ANNOUPDATE", "OBJECTSCALE", "ANNOAUTOSCALE"],
    },
    en: {
      short: "Sets the current annotation scale (CANNOSCALE).",
      long: "The CANNOSCALE command sets the value of the CANNOSCALE system variable — the document's current annotation scale. This scale governs: (1) which annotative INSERTs are visible (only those whose annotationScales list contains the current scaleName — see OBJECTSCALE); (2) the scale factor used to recompute scaleX/scaleY of annotative INSERTs on the next ANNOUPDATE. Also shown in the AutoCAD status bar. The command dispatches header/set-cannoscale-and-sync (PR-8): in addition to updating header.cannoscale it also materializes the new scale across every annotative entity in a single transaction (single undo step) when ANNOAUTOSCALE is enabled. Behaviour fully matches selecting the scale via the AnnotationStatusBar dropdown.",
      examples: ["CANNOSCALE 1:50", "CANNOSCALE 1:100"],
      seeAlso: ["ANNOUPDATE", "OBJECTSCALE", "ANNOAUTOSCALE"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.cannoscale.label",
    labelKeyEn: "command.cannoscale.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: "CS",
  },
  reducerLocations: [
    {
      commandTypeLiteral: "header/set-cannoscale-and-sync",
      file: REDUCER_GEOMETRY,
    },
  ],
};

// ─── PR-8 — Annotative status-bar / scale-list commands ─────────────────────
// These five descriptors mirror the AutoCAD status-bar buttons sitting next to
// CANNOSCALE and the SCALELISTEDIT dialog launcher. RU aliases follow AutoCAD
// CIS terminology (АННОСБРОС / АВТОМАСШТАБ / АННОВИДИМОСТЬ / АННОМОНИТОР /
// СПИСМАСШТАБОВ). All five are status: "implemented" — their dispatcher
// targets exist in editor.ts (PR-4 toggles, PR-7 add/remove-context) and are
// already routed through the reducer + needsShellResyncFromGeometry list in
// editorStore.ts (lines 1465-1497).
//
// SCALELISTEDIT is intentionally a no-op-with-audit on the command line until
// PR-9 lands the ScaleListEditDialog UI. The status-bar button (PR-4) is
// already disabled with a "coming soon" tooltip; the CLI mirror dispatches a
// telemetry-only audit event by re-using `geometry/annoupdate` as a harmless
// no-side-effect target (the reducer is idempotent for documents without
// annotative INSERTs needing rescale). PR-9 will replace this dispatcher with
// the proper `system/open-scalelist-dialog` shell action.

const ANNORESET: CommandDescriptor = {
  id: "ANNORESET",
  category: "system",
  status: "implemented",
  // Annotative-scale position refresh/reset (regeneration class) — not
  // AI-callable.
  aiCallable: false,
  aliases: { en: ["ANNORESET"], ru: ["АННОСБРОС"] },
  commandTypeLiterals: ["geometry/annoupdate"],
  parameters: [],
  help: {
    ru: {
      short:
        "Возвращает позиции альтернативных контекстов аннотативных сущностей к мастер-геометрии.",
      long: "Команда АННОСБРОС (ANNORESET) сбрасывает индивидуальные сдвиги текста / точек размеров / выносок, накопленные в дополнительных annotative-контекстах выбранных сущностей, обратно к позициям, вычисляемым из мастер-сущности. Используется когда пользователь вручную перетащил текст размера на масштабе 1:50, а затем хочет вернуть его «как было» — без потери самого scale-контекста. На текущей фазе PR-8 диспатчит geometry/annoupdate (ближайшая семантически команда: пересчёт annotative-производных полей от мастера). Более точечный entity/annoreset action будет добавлен в последующих PR'ах вместе с дочерним диалогом «Сбросить контексты». Аналог AutoCAD ANNORESET.",
      examples: ["ANNORESET", "АННОСБРОС"],
      seeAlso: ["ANNOUPDATE", "OBJECTSCALE", "CANNOSCALE"],
    },
    en: {
      short:
        "Re-syncs per-scale alternate context positions of annotative entities to the master geometry.",
      long: "The ANNORESET command discards individual offsets (text / dimension-text / leader-line repositioning) accumulated in the alternate annotative contexts of the selected entities, restoring positions derived from the master entity. Useful when the user manually dragged the dim text on scale 1:50 and now wants the default placement back — without losing the scale context itself. In PR-8 the command dispatches geometry/annoupdate (the closest semantic action — recomputing annotative-derived fields from the master). A more granular entity/annoreset action with its companion dialog will be added in later PRs. Mirrors AutoCAD ANNORESET.",
      examples: ["ANNORESET"],
      seeAlso: ["ANNOUPDATE", "OBJECTSCALE", "CANNOSCALE"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.annoreset.label",
    labelKeyEn: "command.annoreset.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: "AR",
  },
  reducerLocations: [
    { commandTypeLiteral: "geometry/annoupdate", file: REDUCER_GEOMETRY },
  ],
};

const SCALELISTEDIT: CommandDescriptor = {
  id: "SCALELISTEDIT",
  category: "system",
  status: "implemented",
  // Opens the scale-list dialog (UI surface) — not AI-callable.
  aiCallable: false,
  aliases: { en: ["SCALELISTEDIT"], ru: ["СПИСМАСШТАБОВ"] },
  // PR-9 — swap from the placeholder geometry/annoupdate dispatcher to
  // the proper system/open-scalelist-dialog shell action. Routing:
  // shellUiReducer.ts emits an audit entry; App.tsx command-line
  // listener intercepts the matching command and opens
  // ScaleListEditDialog (same pattern as RENAME / PURGE).
  commandTypeLiterals: ["system/open-scalelist-dialog"],
  parameters: [],
  help: {
    ru: {
      short:
        "Открывает редактор списка масштабов аннотаций (добавление, удаление, сброс).",
      long: "Команда СПИСМАСШТАБОВ (SCALELISTEDIT) открывает диалог управления списком annotation scales: добавление пользовательских масштабов с произвольным именем и factor'ом, удаление, сброс к списку по умолчанию (зависит от MEASUREMENT — metric или imperial). PR-9 поднимает полнофункциональный диалог: tabular CRUD UI с кнопками Add / Edit / Delete / Move Up / Move Down / Reset. Команда диспатчит system/open-scalelist-dialog (shell-only event); App.tsx поднимает ScaleListEditDialog поверх редактора. Изменения в catalogue немедленно отражаются в выпадающем списке AnnotationStatusBar и сохраняются через DXF export. Аналог AutoCAD SCALELISTEDIT.",
      examples: ["SCALELISTEDIT", "СПИСМАСШТАБОВ"],
      seeAlso: ["CANNOSCALE", "OBJECTSCALE"],
    },
    en: {
      short:
        "Opens the annotation scale list editor (add, remove, reset).",
      long: "The SCALELISTEDIT command opens a dialog that manages the annotation scale list: add user-defined scales with arbitrary name and factor, remove existing entries, reset to defaults (driven by MEASUREMENT — metric or imperial). PR-9 ships the fully-functional dialog with a tabular CRUD UI and Add / Edit / Delete / Move Up / Move Down / Reset buttons. The command dispatches system/open-scalelist-dialog (shell-only event); App.tsx mounts ScaleListEditDialog over the editor. Catalogue mutations are reflected in the AnnotationStatusBar dropdown immediately and persist through DXF export. Mirrors AutoCAD SCALELISTEDIT.",
      examples: ["SCALELISTEDIT"],
      seeAlso: ["CANNOSCALE", "OBJECTSCALE"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.scalelistedit.label",
    labelKeyEn: "command.scalelistedit.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: "SL",
  },
  reducerLocations: [
    {
      commandTypeLiteral: "system/open-scalelist-dialog",
      file: "apps/web/src/app/state/shellUiReducer.ts",
    },
  ],
};

const ANNOMONITOR: CommandDescriptor = {
  id: "ANNOMONITOR",
  category: "system",
  status: "implemented",
  // System-variable toggle (display/monitor state) — not AI-callable.
  aiCallable: false,
  aliases: { en: ["ANNOMONITOR"], ru: ["АННОМОНИТОР"] },
  commandTypeLiterals: ["header/toggle-annomonitor"],
  parameters: [],
  help: {
    ru: {
      short:
        "Включает/выключает монитор аннотаций — подсветку рассинхронизированных аннотативных сущностей.",
      long: "Команда АННОМОНИТОР (ANNOMONITOR) переключает значение системной переменной ANNOMONITOR (boolean). При включённом мониторе рендерер подсвечивает annotative-сущности, у которых master-геометрия рассинхронизирована с текущим scale-контекстом (например, исходный текст изменили после того как был зафиксирован контекст для 1:50). В строке состояния появляется warning-badge, аналогичный AutoCAD. Команда диспатчит header/toggle-annomonitor; reducer инвертирует header.annoMonitor. Дублирует переключатель в AnnotationStatusBar.",
      examples: ["ANNOMONITOR", "АННОМОНИТОР"],
      seeAlso: ["ANNOUPDATE", "ANNORESET", "CANNOSCALE"],
    },
    en: {
      short:
        "Toggles the annotation monitor — highlights out-of-sync annotative entities.",
      long: "The ANNOMONITOR command flips the ANNOMONITOR system variable (boolean). When the monitor is on the renderer highlights annotative entities whose master geometry has drifted out of sync with the current scale context (e.g. the master text was edited after a 1:50 context had been frozen). A warning badge shows in the status bar, mirroring AutoCAD. Dispatches header/toggle-annomonitor; the reducer inverts header.annoMonitor. Duplicates the toggle in AnnotationStatusBar.",
      examples: ["ANNOMONITOR"],
      seeAlso: ["ANNOUPDATE", "ANNORESET", "CANNOSCALE"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.annomonitor.label",
    labelKeyEn: "command.annomonitor.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: "AM",
  },
  reducerLocations: [
    {
      commandTypeLiteral: "header/toggle-annomonitor",
      file: REDUCER_GEOMETRY,
    },
  ],
};

const ANNOALLVISIBLE: CommandDescriptor = {
  id: "ANNOALLVISIBLE",
  category: "system",
  status: "implemented",
  // System-variable toggle (display state) — not AI-callable.
  aiCallable: false,
  aliases: { en: ["ANNOALLVISIBLE"], ru: ["АННОВИДИМОСТЬ"] },
  commandTypeLiterals: ["header/toggle-annoallvisible"],
  parameters: [],
  help: {
    ru: {
      short:
        "Включает/выключает видимость аннотативных сущностей для всех масштабов.",
      long: "Команда АННОВИДИМОСТЬ (ANNOALLVISIBLE) переключает значение системной переменной ANNOALLVISIBLE (boolean). При включённой опции видны annotative-сущности независимо от того, есть ли у них контекст для текущего CANNOSCALE — отображается мастер-форма. При выключенной опции скрываются annotative-сущности без контекста для текущего масштаба (стандартное поведение AutoCAD при ANNOALLVISIBLE=0). Команда диспатчит header/toggle-annoallvisible; reducer инвертирует header.annoAllVisible. Дублирует переключатель в AnnotationStatusBar.",
      examples: ["ANNOALLVISIBLE", "АННОВИДИМОСТЬ"],
      seeAlso: ["ANNOAUTOSCALE", "ANNOMONITOR", "CANNOSCALE"],
    },
    en: {
      short:
        "Toggles visibility of annotative entities across all scales.",
      long: "The ANNOALLVISIBLE command flips the ANNOALLVISIBLE system variable (boolean). When ON, annotative entities are visible regardless of whether they have a context for the current CANNOSCALE — the master form is rendered. When OFF, annotative entities without a context for the current scale are hidden (default AutoCAD behaviour with ANNOALLVISIBLE=0). Dispatches header/toggle-annoallvisible; the reducer inverts header.annoAllVisible. Duplicates the toggle in AnnotationStatusBar.",
      examples: ["ANNOALLVISIBLE"],
      seeAlso: ["ANNOAUTOSCALE", "ANNOMONITOR", "CANNOSCALE"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.annoallvisible.label",
    labelKeyEn: "command.annoallvisible.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: "AV",
  },
  reducerLocations: [
    {
      commandTypeLiteral: "header/toggle-annoallvisible",
      file: REDUCER_GEOMETRY,
    },
  ],
};

const ANNOAUTOSCALE: CommandDescriptor = {
  id: "ANNOAUTOSCALE",
  category: "system",
  status: "implemented",
  // System-variable toggle (auto-scale state) — not AI-callable.
  aiCallable: false,
  aliases: { en: ["ANNOAUTOSCALE"], ru: ["АВТОМАСШТАБ"] },
  commandTypeLiterals: ["header/toggle-annoautoscale"],
  parameters: [],
  help: {
    ru: {
      short:
        "Включает/выключает автомасштаб аннотаций при смене текущего масштаба (CANNOSCALE).",
      long: "Команда АВТОМАСШТАБ (ANNOAUTOSCALE) переключает sign бит системной переменной ANNOAUTOSCALE (диапазон -4..4 по спецификации AutoCAD; абсолютное значение — режим, sign — on/off). При положительном значении при смене CANNOSCALE через header/set-cannoscale-and-sync новый масштаб автоматически добавляется ко всем annotative-сущностям, у которых нет контекста для этого масштаба (так называемая ANNOAUTOSCALE-материализация в одну undo-транзакцию). При отрицательном (или 0) — материализация пропускается, scale меняется только в header. Команда диспатчит header/toggle-annoautoscale; reducer инвертирует sign header.annoAutoScale. Дублирует переключатель в AnnotationStatusBar.",
      examples: ["ANNOAUTOSCALE", "АВТОМАСШТАБ"],
      seeAlso: ["ANNOALLVISIBLE", "CANNOSCALE", "ANNOUPDATE"],
    },
    en: {
      short:
        "Toggles auto-scale — when ON, switching CANNOSCALE adds the new scale to every annotative entity lacking it.",
      long: "The ANNOAUTOSCALE command flips the sign bit of the ANNOAUTOSCALE system variable (AutoCAD spec range -4..4; absolute value = mode, sign = on/off). When positive, switching CANNOSCALE via header/set-cannoscale-and-sync automatically adds the new scale to every annotative entity that lacks a context for it (so-called ANNOAUTOSCALE materialization, performed as a single undo transaction). When negative (or 0) the materialization is skipped and the scale change is header-only. Dispatches header/toggle-annoautoscale; the reducer inverts the sign of header.annoAutoScale. Duplicates the toggle in AnnotationStatusBar.",
      examples: ["ANNOAUTOSCALE"],
      seeAlso: ["ANNOALLVISIBLE", "CANNOSCALE", "ANNOUPDATE"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.annoautoscale.label",
    labelKeyEn: "command.annoautoscale.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: "AA",
  },
  reducerLocations: [
    {
      commandTypeLiteral: "header/toggle-annoautoscale",
      file: REDUCER_GEOMETRY,
    },
  ],
};

const GCCONSTRAINT: CommandDescriptor = {
  id: "GCCONSTRAINT",
  category: "system",
  status: "implemented",
  // DEFAULT-FALSE (flagged for review): adds parametric geometric constraints;
  // advanced parametric feature, no AI safe-contour action, absent from
  // ТЗ §10.1. Fail-closed until decided.
  aiCallable: false,
  aliases: {
    en: ["GCCONSTRAINT", "GEOMCONSTRAINT"],
    ru: ["ГЕОМОГРАН", "ГЕОМОГРАНИЧ"],
  },
  commandTypeLiterals: ["geometry/add-geometric-constraint"],
  draftCommandId: "GCCONSTRAINT",
  draftSteps: [
    {
      stepId: "geom-constraint-pick-first",
      promptKeyRu: "prompt.gcconstraint.pick_first",
      promptKeyEn: "prompt.gcconstraint.pick_first",
      expects: "selection",
    },
    {
      stepId: "geom-constraint-pick-second",
      promptKeyRu: "prompt.gcconstraint.pick_second",
      promptKeyEn: "prompt.gcconstraint.pick_second",
      expects: "selection",
    },
    {
      stepId: "geom-constraint-pick-more",
      promptKeyRu: "prompt.gcconstraint.pick_more",
      promptKeyEn: "prompt.gcconstraint.pick_more",
      expects: "selection",
      keywords: ["ENTER", "ESC"],
    },
    {
      stepId: "geom-constraint-pick-reference-line",
      promptKeyRu: "prompt.gcconstraint.pick_reference_line",
      promptKeyEn: "prompt.gcconstraint.pick_reference_line",
      expects: "selection",
    },
  ],
  parameters: [
    {
      name: "blockName",
      type: "string",
      required: true,
      description: {
        ru: "Имя редактируемого блока (требуется активная BEDIT-сессия).",
        en: "Name of the block being edited (requires an active BEDIT session).",
      },
    },
    {
      name: "constraint",
      type: "object",
      required: true,
      description: {
        ru: "Полная запись GeometricConstraint: { kind, entityIds, optional referenceLineId }. Reducer повторно парсит через geometricConstraintSchema перед применением. Поддерживаемые виды: fix, horizontal, vertical, parallel, perpendicular, tangent, equal, smooth, coincident, collinear, concentric, symmetric.",
        en: "Full GeometricConstraint record: { kind, entityIds, optional referenceLineId }. The reducer re-parses via geometricConstraintSchema before applying. Supported kinds: fix, horizontal, vertical, parallel, perpendicular, tangent, equal, smooth, coincident, collinear, concentric, symmetric.",
      },
    },
  ],
  help: {
    ru: {
      short:
        "Добавляет геометрическое ограничение между выбранными сущностями внутри редактируемого блока.",
      long: "Команда ГЕОМОГРАН (GCCONSTRAINT) добавляет геометрическое ограничение к набору выбранных сущностей внутри активной BEDIT-сессии. Виды и арность picks: arity 1 (fix / horizontal / vertical) — один pick → dispatch; arity 2 (parallel / perpendicular / tangent / equal / smooth) — два pick'а → dispatch; arity ≥2 unbounded (coincident / collinear / concentric) — каждый дополнительный pick добавляется в список, Enter / Esc / <Space> завершает; symmetric — два pick'а + один reference-line pick. Команда не выводится в обычной командной строке (status: 'implemented' но без CLI-нaзвания); запускается через BlockAuthoringPalette → onStartGeometricConstraint, который вызывает startDraftSession('GCCONSTRAINT', { geomKind, geomBlockName }). На завершении dispatch'ится geometry/add-geometric-constraint с собранным constraint object'ом.",
      examples: ["GCCONSTRAINT", "ГЕОМОГРАН"],
      seeAlso: ["BCPARAMETER", "BPARAMETER"],
    },
    en: {
      short:
        "Adds a geometric constraint between picked entities inside the edited block.",
      long: "The GCCONSTRAINT command adds a geometric constraint to a set of picked entities inside an active BEDIT session. Kinds and pick arity: arity 1 (fix / horizontal / vertical) — one pick → dispatch; arity 2 (parallel / perpendicular / tangent / equal / smooth) — two picks → dispatch; arity ≥2 unbounded (coincident / collinear / concentric) — each additional pick adds to the list, Enter / Esc / <Space> commits; symmetric — two picks + one reference-line pick. The command is not exposed on the standard command line (status: 'implemented' but no CLI typed-name route); it is launched via BlockAuthoringPalette → onStartGeometricConstraint which calls startDraftSession('GCCONSTRAINT', { geomKind, geomBlockName }). On completion geometry/add-geometric-constraint dispatches with the assembled constraint object.",
      examples: ["GCCONSTRAINT"],
      seeAlso: ["BCPARAMETER", "BPARAMETER"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.gcconstraint.label",
    labelKeyEn: "command.gcconstraint.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: "GC",
  },
  reducerLocations: [
    {
      commandTypeLiteral: "geometry/add-geometric-constraint",
      file: REDUCER_GEOMETRY,
    },
  ],
};

// ───────────────────────────── LAYER — T7 (agent-bridge) ──────────────────
// AutoCAD LAYER (alias LA) — the Layer Properties Manager: New / Rename (this
// descriptor's two implemented sub-commands, mirrors LAYOUT's SET/NEW/DELETE/
// RENAME shape). Distinct AutoCAD command from CHPROP/PROPERTIES: LAYER
// authors the LAYER TABLE itself (create a new layer row; rename an existing
// row, cascading every entity still "on" it to the renamed row so it stays
// on the SAME layer) — it never reassigns a SELECTED entity to a DIFFERENT
// layer, which is CHPROP/PROPERTIES's `Layer` field (ТЗ §10.1 marks both
// `aiCallable:false`, "reserved for user/UI"; see system.ts:340/397 comments
// below). LAYER is absent from the ТЗ §10.1 matrix entirely (no row at all —
// unlike CHPROP/PROPERTIES's EXPLICIT `no`), so authoring it as a NEW
// aiCallable command is an internal architectural decision (CLAUDE.md source
// #4), not a ТЗ §10.1 flip — the same footing T3's ERASE / T2's created-id
// surfacing stood on. Delete / SET CURRENT / per-layer property edits
// (color/linetype/lineweight/freeze/lock/plot) already have a FULL,
// undo-able, validated UI surface (LayerManagerDialog + the quickbar current-
// layer selector) but are DELIBERATELY NOT exposed here — AI_BRIDGE_EDIT_
// TASKS.md's T7 scope names only CREATE_LAYER / SET_ACTIVE_LAYER / RENAME;
// SET_ACTIVE_LAYER (a current-pointer change, NOT a table edit) is wired
// through the ALREADY-`aiCallable:true` PROPERTIES-internal `properties/apply`
// mechanic (see `layerTranslators.ts` — mirrors SET_CANNOSCALE / the DIMSTYLE
// "Set Current" precedent) rather than a new command-type literal, so it is
// NOT one of this descriptor's `commandTypeLiterals`.
const LAYER: CommandDescriptor = {
  id: "LAYER",
  category: "system",
  status: "implemented",
  // T7 (agent-bridge layer-mgmt) — NEW aiCallable command (absent from ТЗ
  // §10.1's matrix; not the CHPROP/PROPERTIES-gated entity-property channel —
  // see the module comment above).
  aiCallable: true,
  aliases: { en: ["LAYER", "LA"], ru: ["СЛОЙ", "СЛ"] },
  commandTypeLiterals: ["header/add-layer", "header/rename-layer"],
  parameters: [
    {
      name: "subCommand",
      type: "enum",
      enumValues: ["NEW", "RENAME"],
      required: true,
      description: {
        ru: "Подкоманда: NEW (создать новый слой), RENAME (переименовать существующий слой, каскадно на все сущности этого слоя). Delete / Set Current / правка свойств слоя выполняются через диспетчер слоёв (LayerManagerDialog), не через эту команду.",
        en: "Sub-command: NEW (create a new layer), RENAME (rename an existing layer, cascading to every entity on it). Delete / Set Current / layer property edits go through the Layer Manager dialog, not this command.",
      },
    },
    {
      name: "name",
      type: "string",
      required: false,
      description: {
        ru: "Имя нового слоя (для NEW). Отклоняются пустые имена и дубликаты (регистронезависимо — «Walls» и «walls» считаются ОДНИМ слоем, как в AutoCAD/DXF).",
        en: "Name of the new layer (for NEW). Empty names and duplicates (case-insensitive — \"Walls\" and \"walls\" name the SAME layer, matching AutoCAD/DXF) are rejected.",
      },
    },
    {
      name: "oldName",
      type: "string",
      required: false,
      description: {
        ru: "Текущее имя переименовываемого слоя (для RENAME, регистронезависимый поиск). Слой «0» переименовать нельзя.",
        en: "Current name of the layer being renamed (for RENAME, matched case-insensitively). Layer \"0\" cannot be renamed.",
      },
    },
    {
      name: "newName",
      type: "string",
      required: false,
      description: {
        ru: "Новое имя слоя (для RENAME). Должно быть уникальным (регистронезависимо) и не «0» (слой «0» обязателен и уже занят); пустые значения отклоняются.",
        en: "New layer name (for RENAME). Must be unique (case-insensitive) and cannot be \"0\" (layer \"0\" is mandatory and always occupies that name); empty values are rejected.",
      },
    },
  ],
  help: {
    ru: {
      short: "Создаёт и переименовывает слои чертежа (диспетчер слоёв).",
      long: "Команда СЛОЙ (LAYER) управляет таблицей слоёв чертежа. Реализованные сценарии: NEW — создать новый слой с именем по умолчанию (цвет 7, тип линии Continuous, толщина «По умолчанию», включён/не заморожен/не заблокирован/печатается — как AutoCAD LAYER New); RENAME — переименовать существующий слой, каскадно перенося ВСЕ сущности, находящиеся на этом слое (включая сущности внутри определений блоков), на переименованную запись (та же семантика, что и переименование DIMSTYLE) — слой «0» переименовать нельзя. Это НЕ команда переноса выбранных сущностей на другой слой — для этого служат CHPROP/PROPERTIES (палитра свойств), которые ИИ-агенту недоступны по ТЗ §10.1. СЕГОДНЯ вызывается через ИИ-агента (CREATE_LAYER/RENAME_LAYER) или через диспетчер слоёв (LayerManagerDialog, кнопка на панели) — обе поверхности используют один и тот же store-контур, всё отменяемо через Ctrl+Z. Ввод `LAYER NEW <имя>` / `LAYER RENAME <старое> <новое>` строкой командной строки с аргументами ПОКА НЕ реализован (только голое имя команды открывает диспетчер слоёв, как RENAME/PURGE); удаление слоя, назначение текущего слоя (CLAYER) и правка свойств отдельного слоя (цвет/тип линии/толщина/заморозка/блокировка/печать) выполняются исключительно через диспетчер слоёв.",
      examples: ["LAYER", "СЛОЙ"],
      seeAlso: ["CHPROP", "PROPERTIES", "LAYOUT"],
    },
    en: {
      short: "Creates and renames drawing layers (Layer Manager).",
      long: "The LAYER command manages the drawing's layer table. Implemented today: NEW — create a new layer with AutoCAD's LAYER-New defaults (color 7, linetype Continuous, lineweight Default, on/thawed/unlocked/plottable); RENAME — rename an existing layer, cascading EVERY entity currently on that layer (including entities inside block definitions) to the renamed row (the same semantics as a DIMSTYLE rename) — layer \"0\" cannot be renamed. This is NOT the command for moving a SELECTED entity to a different layer — that is CHPROP/PROPERTIES (the Properties palette), which ТЗ §10.1 marks not AI-callable. TODAY it is invoked via the AI agent (CREATE_LAYER/RENAME_LAYER) or the Layer Manager dialog (toolbar button) — both surfaces route through the SAME store contour, fully undoable via Ctrl+Z. Typed command-line argument entry (`LAYER NEW <name>` / `LAYER RENAME <old> <new>`) is NOT yet wired (only the bare command name opens the Layer Manager, matching RENAME/PURGE); deleting a layer, setting the current layer (CLAYER), and editing a single layer's properties (color/linetype/lineweight/freeze/lock/plot) go through the Layer Manager dialog exclusively.",
      examples: ["LAYER", "LA"],
      seeAlso: ["CHPROP", "PROPERTIES", "LAYOUT"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.layer.label",
    labelKeyEn: "command.layer.label",
    ribbonTab: null,
    ribbonSection: "ribbon.section.properties",
    glyph: "LA",
  },
  reducerLocations: [
    { commandTypeLiteral: "header/add-layer", file: REDUCER_STYLE_TABLES },
    { commandTypeLiteral: "header/rename-layer", file: REDUCER_STYLE_TABLES },
  ],
};

// ─── BLOCKPROPERTIESMANAGER (BPM) — Phase N.5 ────────────────────────
// Drawing-wide manager for every dynamic-block parameter / action /
// visibility-state / attribute. Mirrors AutoCAD's
// BLOCKPROPERTIESMANAGER (alias BPM, RU ДСБЛОК) command. Operates
// outside an active BEDIT session — the dialog lists every block in
// `document.blocks` and lets the user audit / rename / reorder
// metadata without entering BEDIT for each one.
const BLOCKPROPERTIESMANAGER: CommandDescriptor = {
  id: "BLOCKPROPERTIESMANAGER",
  category: "system",
  status: "implemented",
  // Opens the block-properties manager dialog (UI surface) — not AI-callable.
  aiCallable: false,
  aliases: {
    en: ["BLOCKPROPERTIESMANAGER", "BPM"],
    ru: ["ДСБЛОК"],
  },
  commandTypeLiterals: [
    "geometry/rename-block-parameter",
    "geometry/update-block-parameter-description",
    "geometry/toggle-parameter-show-in-palette",
    "geometry/reorder-block-parameters",
    "geometry/rename-block-action",
    "geometry/update-block-action-description",
    "geometry/reorder-block-actions",
  ],
  parameters: [],
  help: {
    ru: {
      short:
        "Открывает диспетчер свойств блока для управления параметрами / действиями / видимостью всех блоков чертежа.",
      long: "Команда ДСБЛОК (BLOCKPROPERTIESMANAGER, BPM) открывает диалог Block Properties Manager — централизованный редактор всех параметров, действий, состояний видимости и атрибутов блоков чертежа. Диалог показывает список определений блоков слева и набор вкладок (Параметры / Действия / Видимость / Атрибуты) справа для выбранного блока. Поддерживает inline-редактирование имени и описания параметра / действия, переключение колонки «В палитре свойств», переупорядочивание через кнопки Up/Down, удаление с предупреждением о потере связанных действий. В отличие от BEDIT-сессии диалог работает вне block-edit пространства — пользователь может управлять любым блоком без входа в редактор блока.",
      examples: ["ДСБЛОК", "BPM", "BLOCKPROPERTIESMANAGER"],
      seeAlso: ["BEDIT", "BPARAMETER", "BACTION", "BVSTATE", "BATTMAN"],
    },
    en: {
      short:
        "Opens the Block Properties Manager to edit parameters / actions / visibility across every block in the drawing.",
      long: "The BLOCKPROPERTIESMANAGER command (alias BPM, RU ДСБЛОК) opens the Block Properties Manager — a centralized editor for parameters, actions, visibility states and attributes across every block definition in the drawing. The dialog lists block definitions in a left sidebar and shows a set of tabs (Parameters / Actions / Visibility / Attributes) for the selected block. Inline editing supports name + description for parameters / actions, the AutoCAD parity 'Show in Properties palette' toggle, reorder via Up / Down buttons and delete with a warning about orphaned actions. Unlike a BEDIT session the dialog works outside block-edit — the user can audit any block without first entering the editor.",
      examples: ["BPM", "BLOCKPROPERTIESMANAGER"],
      seeAlso: ["BEDIT", "BPARAMETER", "BACTION", "BVSTATE", "BATTMAN"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.bpm.label",
    labelKeyEn: "command.bpm.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: "BPM",
  },
  reducerLocations: [
    {
      commandTypeLiteral: "geometry/rename-block-parameter",
      file: REDUCER_GEOMETRY,
    },
    {
      commandTypeLiteral: "geometry/update-block-parameter-description",
      file: REDUCER_GEOMETRY,
    },
    {
      commandTypeLiteral: "geometry/toggle-parameter-show-in-palette",
      file: REDUCER_GEOMETRY,
    },
    {
      commandTypeLiteral: "geometry/reorder-block-parameters",
      file: REDUCER_GEOMETRY,
    },
    {
      commandTypeLiteral: "geometry/rename-block-action",
      file: REDUCER_GEOMETRY,
    },
    {
      commandTypeLiteral: "geometry/update-block-action-description",
      file: REDUCER_GEOMETRY,
    },
    {
      commandTypeLiteral: "geometry/reorder-block-actions",
      file: REDUCER_GEOMETRY,
    },
  ],
};

// ─── BASE (БАЗА) — drawing insertion base point ($INSBASE) ───────────
// AutoCAD's BASE command sets the drawing-level insertion base point — the
// DXF HEADER `$INSBASE` system variable (group codes 10/20/30 per the
// Autodesk DXF reference + ezdxf; NOT the per-layout AcDbLayout 12/22/32
// field). This is the origin AutoCAD uses when the drawing is inserted as a
// block into another drawing; BASE does not move any geometry. Single
// point-pick command-line flow (AutoCAD "Enter base point <0,0,0>:").
// Dispatches `header/set-insertion-base`, handled by the geometry reducer
// (applySetInsertionBase) and reversible via computeInverse.
const BASE: CommandDescriptor = {
  id: "BASE",
  category: "system",
  status: "implemented",
  // BASE / $INSBASE is absent from the ТЗ §10.1 command matrix. Per the
  // fail-closed default (cf. OBJECTSCALE / GCCONSTRAINT, also absent from
  // §10.1) it is NOT AI-callable — it is a document-level header sysvar
  // setter, not geometry creation/modification the AI safe contour drives.
  aiCallable: false,
  aliases: { en: ["BASE"], ru: ["БАЗА"] },
  commandTypeLiterals: ["header/set-insertion-base"],
  draftCommandId: "BASE",
  draftSteps: [
    {
      stepId: "insbase-point",
      promptKeyRu: "prompt.base.point",
      promptKeyEn: "prompt.base.point",
      expects: "point",
    },
  ],
  parameters: [
    {
      name: "point",
      type: "point3d",
      required: true,
      description: {
        ru: "Базовая точка вставки чертежа ($INSBASE). 3D-точка {x, y, z}; z по умолчанию 0. Это начало координат, которое AutoCAD использует при вставке данного чертежа как блока в другой чертёж. Геометрию не перемещает.",
        en: "Drawing insertion base point ($INSBASE). 3D point {x, y, z}; z defaults to 0. This is the origin AutoCAD uses when this drawing is inserted as a block into another drawing. Does not move any geometry.",
      },
    },
  ],
  help: {
    ru: {
      short: "Задаёт базовую точку вставки чертежа ($INSBASE).",
      long: "Команда БАЗА (BASE) устанавливает базовую точку вставки чертежа — системную переменную DXF HEADER $INSBASE (групповые коды 10/20/30). Это начало координат, относительно которого позиционируется чертёж, когда его вставляют как блок в другой чертёж. По умолчанию $INSBASE = (0,0,0). Команда запрашивает одну точку в командной строке (как AutoCAD: «Укажите базовую точку <0,0,0>:») и записывает её в модель через Command Engine (header/set-insertion-base). Геометрия НЕ перемещается — меняется только опорная точка. Операция обратима: отмена (UNDO) восстанавливает предыдущую базовую точку одним шагом. Значение сохраняется при экспорте в DXF (round-trip через HEADER). Не путать с per-layout базовой точкой AcDbLayout (коды 12/22/32) — это другое поле.",
      examples: ["BASE", "БАЗА", "BASE 100,50", "БАЗА 0,0,0"],
      seeAlso: ["INSERT", "BLOCK"],
    },
    en: {
      short: "Sets the drawing insertion base point ($INSBASE).",
      long: "The BASE command sets the drawing's insertion base point — the DXF HEADER $INSBASE system variable (group codes 10/20/30). This is the origin against which the drawing is positioned when inserted as a block into another drawing. $INSBASE defaults to (0,0,0). The command prompts for a single point on the command line (AutoCAD: \"Enter base point <0,0,0>:\") and writes it to the model through the Command Engine (header/set-insertion-base). No geometry is moved — only the reference origin changes. The operation is reversible: UNDO restores the previous base point in a single step. The value persists on DXF export (round-trips through the HEADER). Not to be confused with the per-layout AcDbLayout insertion base (codes 12/22/32), which is a different field.",
      examples: ["BASE", "BASE 100,50", "BASE 0,0,0"],
      seeAlso: ["INSERT", "BLOCK"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.base.label",
    labelKeyEn: "command.base.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: "BA",
  },
  reducerLocations: [
    { commandTypeLiteral: "header/set-insertion-base", file: REDUCER_GEOMETRY },
  ],
};

// COLOR / -COLOR / SETCOLOR / ЦВЕТ — set the CURRENT entity colour (CECOLOR),
// the colour AutoCAD assigns to NEWLY-created entities. Sets the DXF HEADER
// `$CECOLOR` system variable (group 62). COLOR / SETCOLOR / ЦВЕТ (no value) open
// the "Select Color" dialog; -COLOR (no value) runs the dialog-less command-line
// keyword prompt; with a value (ACI / #hex / R,G,B / ByLayer / ByBlock) it sets
// the colour directly. Dispatches `header/set-cecolor`, handled by the geometry
// reducer (applySetCeColor) and reversible via computeInverse. AI-callable: the
// AI safe contour's SET_CURRENT_COLOR action targets this command, emitting
// `header/set-cecolor` through the guarded translator (never writes entities).
const COLOR: CommandDescriptor = {
  id: "COLOR",
  category: "system",
  status: "implemented",
  aiCallable: true,
  aliases: { en: ["COLOR", "COL", "-COLOR", "SETCOLOR"], ru: ["ЦВЕТ"] },
  commandTypeLiterals: ["header/set-cecolor"],
  parameters: [
    {
      name: "color",
      type: "string",
      required: false,
      description: {
        ru: "Текущий цвет для новых объектов (CECOLOR / $CECOLOR). Значение: ПОСЛОЮ (ByLayer) / ПОБЛОКУ (ByBlock) / 0 / 256 / индекс 1..255 / #rrggbb / «R,G,B» (истинный цвет). Без значения COLOR/SETCOLOR/ЦВЕТ открывают диалог «Выбор цвета», а -COLOR запрашивает цвет в командной строке. Существующие объекты не меняются — меняется только текущий цвет.",
        en: "Current colour for new objects (CECOLOR / $CECOLOR). Value: ByLayer / ByBlock / 0 / 256 / index 1..255 / #rrggbb / `R,G,B` (true colour). With no value COLOR/SETCOLOR/ЦВЕТ open the Select Color dialog and -COLOR prompts on the command line. Existing objects are NOT changed — only the current colour.",
      },
    },
  ],
  help: {
    ru: {
      short: "Задаёт текущий цвет для новых объектов (CECOLOR).",
      long: "Команда ЦВЕТ (COLOR; псевдонимы COL / -COLOR / SETCOLOR) устанавливает ТЕКУЩИЙ цвет — системную переменную DXF HEADER $CECOLOR (групповой код 62), который наследуют ВНОВЬ создаваемые объекты. Поддерживаются формы: ПОСЛОЮ (ByLayer, по умолчанию), ПОБЛОКУ (ByBlock), индекс цвета 1..255, истинный цвет (#rrggbb или «R,G,B»). Без значения COLOR/SETCOLOR/ЦВЕТ открывают диалог «Выбор цвета» (как в AutoCAD), а -COLOR запрашивает значение в командной строке. Команда НЕ перекрашивает существующие объекты — меняется только текущий цвет. Значение сохраняется при экспорте в DXF (round-trip через HEADER $CECOLOR) и обратимо через отмену (UNDO).",
      examples: ["ЦВЕТ", "COLOR 5", "ЦВЕТ #ff8000", "SETCOLOR 255,128,0", "-COLOR"],
      seeAlso: ["CHPROP", "MATCHPROP", "PROPERTIES"],
    },
    en: {
      short: "Sets the current colour for new objects (CECOLOR).",
      long: "The COLOR command (aliases COL / -COLOR / SETCOLOR; RU ЦВЕТ) sets the CURRENT colour — the DXF HEADER $CECOLOR system variable (group code 62) — that NEWLY-created entities inherit. Accepted forms: ByLayer (the default), ByBlock, a colour index 1..255, and true colour (#rrggbb or `R,G,B`). With no value COLOR/SETCOLOR/ЦВЕТ open the Select Color dialog (AutoCAD parity) and -COLOR prompts for the value on the command line. The command does NOT recolour existing objects — only the current colour changes. The value persists on DXF export (round-trips through the HEADER $CECOLOR) and is reversible via UNDO.",
      examples: ["COLOR", "COLOR 5", "COLOR #ff8000", "SETCOLOR 255,128,0", "-COLOR"],
      seeAlso: ["CHPROP", "MATCHPROP", "PROPERTIES"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.color.label",
    labelKeyEn: "command.color.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: "CO",
  },
  reducerLocations: [
    { commandTypeLiteral: "header/set-cecolor", file: REDUCER_GEOMETRY },
  ],
};

// ─── PAN / ZOOM — view-navigation (u043, ТЗ §10.1 AI=yes) ──────────────────
//
// PAN and ZOOM are pure VIEW operations: they move/scale the camera and never
// mutate geometry, so they do NOT pass through `editorCommandSchema` /
// `geometryShellReducer`. Their command-line handlers live in `App.tsx`
// (`submitCommandText`) and drive the framework-agnostic camera API
// (`scene3d/cameraApi.ts` / `cameraApiAdapter.ts`); telemetry is a `view-op`
// audit event via `editorStore.recordViewOp` (ТЗ §14). Consequently
// `commandTypeLiterals` + `reducerLocations` are intentionally EMPTY — there is
// no reducer command literal to reference (E.ORPHAN passes on the empty set;
// the view-op path is the source of truth, not a geometry command). Both are
// `aiCallable: true` per the ТЗ §10.1 matrix (PAN + ZOOM = AI=yes), so the AI
// safe contour MAY target them once a view-dispatch action is wired (the AI
// generation/dispatch arm is a separate CC-2c slice — these descriptors make
// the commands REACHABLE, fail-closed until then).

const PAN: CommandDescriptor = {
  id: "PAN",
  category: "system",
  status: "implemented",
  // ТЗ §10.1 — PAN is AI=yes (a legitimate view-navigation command the AI may
  // target through the safe contour). The realtime drag handler + view-op
  // telemetry exist (App.tsx PAN branch + scene3d onPanComplete → recordViewOp),
  // and the App.tsx PAN router now also drives a scripted displacement form
  // (`-PAN x1,y1 x2,y2` → StageCameraApi.pan) with the same honest view-op
  // gating. The AI generation/dispatch arm itself stays fail-closed (CC-2c).
  aiCallable: true,
  // EN: PAN + the -PAN command-line form + the acad.pgp `P` alias. RU: CIS
  // command name ПАН (the form the App.tsx handler already accepts).
  aliases: { en: ["PAN", "-PAN", "P"], ru: ["ПАН"] },
  commandTypeLiterals: [],
  // PAN exposes an OPTIONAL `displacement` point2d describing the view-shift
  // VECTOR (x2-x1, y2-y1). IMPORTANT shape note (cmd-pan FINDING 2): the CLI
  // parser today accepts ONLY the TWO-point form `-PAN x1,y1 x2,y2` (a base
  // point and a destination point, four numbers), from which the App.tsx PAN
  // router derives this displacement vector and calls `StageCameraApi.pan()`.
  // The single-vector `displacement` advertised here is the DEFERRED CC-2c AI
  // dispatch form: the AI safe contour would emit one displacement vector, but
  // the generation/dispatch arm is NOT wired yet (fail-closed, exactly as
  // ZOOM's — exposing the shape does NOT claim an end-to-end AI→camera pan,
  // #R3). The descriptor stays HONEST about the gap: bare verb (PAN/-PAN/P/ПАН)
  // = interactive realtime panning; CLI = two points; this single-vector param
  // = the future AI form. Keeping them aligned avoids claiming a shape the
  // parser does not yet accept.
  parameters: [
    {
      name: "displacement",
      type: "point2d",
      required: false,
      description: {
        ru: "Вектор смещения вида (x2-x1, y2-y1) — отложенная AI-форма (CC-2c, диспетчер ещё не подключён). В командной строке сегодня принимается ТОЛЬКО двухточечная форма -PAN x1,y1 x2,y2 (базовая точка → точка назначения, 4 числа), из которой вычисляется этот вектор. Пусто/голая команда — интерактивное панорамирование в реальном времени.",
        en: "View-shift vector (x2-x1, y2-y1) — the DEFERRED AI form (CC-2c, dispatch arm not wired yet). The command line today accepts ONLY the two-point form -PAN x1,y1 x2,y2 (base point → destination point, 4 numbers), from which this vector is derived. Omit / use the bare verb for interactive realtime panning.",
      },
    },
  ],
  help: {
    ru: {
      short: "Панорамирует вид без изменения масштаба (перемещает камеру).",
      long: "Команда ПАН (PAN / -PAN; alias P) сдвигает текущий вид без изменения масштаба отображения — перемещает центр камеры в плоскости экрана. Двухточечная форма -PAN x1,y1 x2,y2 смещает вид на вектор (x2-x1, y2-y1) и записывает один view-op `pan`; предыдущий вид кладётся в кольцо ZOOM Previous (отменяется через ZOOM Previous, а не Ctrl+Z). Голая форма (PAN/ПАН/P) или пункт «Панорамирование» в контекстном меню включает интерактивное панорамирование в реальном времени (перетаскивание ЛКМ; курсор-рука; Esc/Enter — выход). Интерактивный жест также доступен средней кнопкой мыши (СКМ) или двупальцевым жестом на тачскрине; по завершении жеста записывается ровно один телеметрический view-op `pan` с накопленным смещением камеры в единицах чертежа (НЕ на каждый кадр — как ZOOM/REGEN). Внутри плавающего видового экрана (MSPACE_VIA_VP) панорамирование перенаправляется в модельный вид этого ВЭ (команда geometry/viewport-set-view со своим аудитом), а не в камеру листа. Геометрия не изменяется — это чистая операция вида (ТЗ §10.1, AI=yes; §14 телеметрия).",
      examples: ["PAN", "ПАН", "P", "-PAN 0,0 100,50"],
      seeAlso: ["ZOOM", "REGEN"],
    },
    en: {
      short: "Pans the view without changing zoom (moves the camera).",
      long: "The PAN command (PAN / -PAN; alias P) shifts the current view without changing the display scale — it moves the camera centre in the screen plane. The two-point form -PAN x1,y1 x2,y2 displaces the view by the vector (x2-x1, y2-y1) and records one `pan` view-op; the pre-pan view is pushed onto the ZOOM Previous ring (reversed with ZOOM Previous, not Ctrl+Z). The bare verb (PAN/-PAN/P/ПАН), or the right-click 'Pan' menu item, enters interactive realtime panning (LMB drag; hand cursor; Esc/Enter exits). The interactive gesture is also available via a middle-mouse-button (MMB) drag or a two-finger touch gesture; on gesture completion exactly one `pan` view-op telemetry event is recorded carrying the cumulative camera displacement in drawing units (NOT per frame — same cadence as ZOOM/REGEN). Inside a floating viewport (MSPACE_VIA_VP) the pan is redirected into that viewport's model view (the geometry/viewport-set-view command, which records its own audit) rather than the paper camera. No geometry is modified — this is a pure view operation (ТЗ §10.1, AI=yes; §14 telemetry).",
      examples: ["PAN", "-PAN", "P", "-PAN 0,0 100,50"],
      seeAlso: ["ZOOM", "REGEN"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.pan.label",
    labelKeyEn: "command.pan.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: "PA",
  },
  // View operation — no geometry reducer; the handler is App.tsx-local and the
  // typed effect is a `view-op` audit event (recordViewOp), not a command.
  reducerLocations: [],
};

const ZOOM: CommandDescriptor = {
  id: "ZOOM",
  category: "system",
  status: "implemented",
  // ТЗ §10.1 — ZOOM is AI=yes. The Extents/All/Window/Previous/Object branches
  // exist (App.tsx ZOOM branch → cameraApiAdapter) with gated view-op telemetry.
  aiCallable: true,
  // EN: ZOOM + the `Z` acad.pgp alias. RU: CIS command name ПОКАЗАТЬ + short ПОК
  // (both accepted by the App.tsx zoom-command regex prefix).
  aliases: { en: ["ZOOM", "Z"], ru: ["ПОКАЗАТЬ", "ПОК"] },
  commandTypeLiterals: [],
  parameters: [
    {
      name: "mode",
      type: "enum",
      enumValues: ["EXTENTS", "ALL", "WINDOW", "PREVIOUS", "OBJECT"],
      required: true,
      description: {
        ru: "Режим зумирования: EXTENTS (E — по границам всех объектов, 10% поля), ALL (A — по лимитам/границам), WINDOW (W — по рамке двумя углами; без аргументов запускает интерактивный выбор рамки), PREVIOUS (P — предыдущий вид из кольца истории, глубина 32), OBJECT (O — по выбранным объектам или указанному id, 20% поля). Подкоманды Realtime/Scale/Center пока не реализованы (запланированы).",
        en: "Zoom mode: EXTENTS (E — fit all entities, 10% padding), ALL (A — fit limits/extents), WINDOW (W — fit a two-corner rectangle; with no args starts an interactive window pick), PREVIOUS (P — restore the previous view from the 32-deep history ring), OBJECT (O — fit the selected entities or a supplied id, 20% padding). The Realtime/Scale/Center sub-options are not implemented yet (scheduled).",
      },
    },
    {
      name: "target",
      type: "string",
      required: false,
      description: {
        ru: "Аргумент режима: для WINDOW два угла `x1,y1 x2,y2` (скриптовая форма без интерактива); для OBJECT — id объекта (без него зумирует по текущему выделению). Для остальных режимов не используется.",
        en: "Mode argument: for WINDOW two corners `x1,y1 x2,y2` (the scripted non-interactive form); for OBJECT an entity id (omit to zoom the current selection). Unused for the other modes.",
      },
    },
  ],
  help: {
    ru: {
      short: "Изменяет масштаб отображения вида (приближает/отдаляет).",
      long: "Команда ПОКАЗАТЬ (ZOOM / Z; короткое ПОК) изменяет масштаб отображения без изменения геометрии. Реализованные режимы (буквенные подкоманды как в AutoCAD): EXTENTS/E — вписать все объекты (поле 10%, фактор 0.9× как в ezdxf); ALL/A — по лимитам (сейчас совпадает с EXTENTS, пока нет отдельного прямоугольника лимитов); WINDOW/W — вписать рамку: либо двумя углами `ZW x1,y1 x2,y2` (скрипт/AI), либо интерактивным выбором двух углов на холсте; PREVIOUS/P — восстановить предыдущий вид из кольца истории (глубина 32, как в AutoCAD); OBJECT/O — вписать выбранные объекты или указанный id (поле 20%), либо интерактивный выбор объекта. Телеметрия: ровно один view-op `zoom` записывается, ТОЛЬКО когда вид реально изменился (пустая история PREVIOUS, ненайденный id OBJECT и старт интерактивного выбора не пишут ложный успех — FIX #269). Подкоманды Realtime, Scale (nX/nXP), Center пока не реализованы. Геометрия не изменяется (ТЗ §10.1, AI=yes; §14).",
      examples: ["ZOOM EXTENTS", "Z E", "ПОКАЗАТЬ E", "ZW 0,0 100,100", "Z P", "ZO ent-42"],
      seeAlso: ["PAN", "REGEN"],
    },
    en: {
      short: "Changes the display scale of the view (zoom in/out).",
      long: "The ZOOM command (ZOOM / Z; CIS ПОКАЗАТЬ / ПОК) changes the display scale without modifying geometry. Implemented modes (AutoCAD-style keyword letters): EXTENTS/E — fit all entities (10% padding, 0.9× factor matching the ezdxf reference); ALL/A — fit limits (currently equals EXTENTS while there is no separate limits rectangle); WINDOW/W — fit a rectangle, either two corners `ZW x1,y1 x2,y2` (script/AI) or an interactive two-corner pick on the canvas; PREVIOUS/P — restore the previous view from the history ring (32 deep, AutoCAD parity); OBJECT/O — fit the selected entities or a supplied id (20% padding), or an interactive object pick. Telemetry: exactly one `zoom` view-op is recorded ONLY when the view actually changed (an empty-history PREVIOUS, a not-found OBJECT id, and the start of an interactive pick record no false success — FIX #269). The Realtime, Scale (nX/nXP) and Center sub-options are not implemented yet. No geometry is modified (ТЗ §10.1, AI=yes; §14).",
      examples: ["ZOOM EXTENTS", "Z E", "ZOOM WINDOW", "ZW 0,0 100,100", "Z P", "ZO ent-42"],
      seeAlso: ["PAN", "REGEN"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.zoom.label",
    labelKeyEn: "command.zoom.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: "Z",
  },
  // View operation — no geometry reducer; the handler is App.tsx-local and the
  // typed effect is a `view-op` audit event (recordViewOp), not a command.
  reducerLocations: [],
};

// REGEN and REGENALL are pure VIEW operations (display-refresh): they
// regenerate the rendered display of the current viewport (REGEN) or every
// viewport / layout (REGENALL) and never mutate geometry. Like PAN/ZOOM their
// command-line handlers live in `App.tsx` (`submitCommandText`) and complete
// SYNCHRONOUSLY, emitting exactly one `view-op` audit event via
// `editorStore.recordViewOp({ opType: "regen" | "regenall" })` (ТЗ §14). They
// therefore reference NO geometry command literal / reducer
// (`commandTypeLiterals` + `reducerLocations` are intentionally EMPTY — the
// view-op path is the source of truth, not a geometry command). UNLIKE PAN/ZOOM
// they are `aiCallable: false`: ТЗ §10.1 marks display-refresh commands as
// AI=no (regenerating the display is a local rendering concern, not a drawing
// operation the AI safe-contour may target). The aliases mirror the LIVE
// App.tsx handler exactly (REGEN: REGEN/RE/РЕГЕН; REGENALL: REGENALL/REA/
// РЕГЕНВСЕ) so these descriptors only ADVERTISE the already-wired behaviour —
// zero behaviour change. `REA *REGENALL` is acad.pgp-canonical.

const REGEN: CommandDescriptor = {
  id: "REGEN",
  category: "system",
  status: "implemented",
  // ТЗ §10.1 — REGEN is a display-refresh view-op (AI=no). The synchronous
  // handler + view-op telemetry exist (App.tsx REGEN branch → recordViewOp).
  aiCallable: false,
  // EN: REGEN + the acad.pgp `RE` alias. RU: CIS command name РЕГЕН (the forms
  // the App.tsx handler already accepts).
  aliases: { en: ["REGEN", "RE"], ru: ["РЕГЕН"] },
  commandTypeLiterals: [],
  // REGEN takes no arguments — it issues a display-refresh view-op for the
  // current viewport (the reactive renderer keeps geometry continuously synced).
  parameters: [],
  help: {
    ru: {
      short: "Обновляет отображение текущего видового экрана (view-op РЕГЕН).",
      long: "Команда РЕГЕН (REGEN / RE) — операция обновления отображения текущего видового экрана (display-refresh view-op, ТЗ §10.1). Обработчик командной строки завершается синхронно и записывает ровно один телеметрический view-op `regen` через `recordViewOp` (ТЗ §14). В отличие от классического AutoCAD, где REGEN перестраивал устаревший display-list, здесь реактивный рендерер непрерывно держит отображаемую геометрию в синхроне с моделью, поэтому отдельный пересчёт или перерисовка кэша этой командой не выполняются. Команда выполняется из командной строки / меню; жесты не задействованы. Геометрия не изменяется. Не вызывается ИИ (display-refresh — AI=no по ТЗ §10.1).",
      examples: ["REGEN", "RE", "РЕГЕН"],
      seeAlso: ["ZOOM", "PAN"],
    },
    en: {
      short: "Refreshes the display of the current viewport (REGEN view-op).",
      long: "The REGEN command (REGEN / RE) is a display-refresh view operation for the current viewport (ТЗ §10.1). The command-line handler completes synchronously and records exactly one `regen` view-op telemetry event via `recordViewOp` (ТЗ §14). Unlike classic AutoCAD, where REGEN rebuilt a stale display list, this reactive renderer keeps the displayed geometry continuously in sync with the model, so the command performs no separate cache recompute or redraw. It is invoked from the command line / menu; no gesture is involved. No geometry is modified. It is NOT AI-callable (a display-refresh is AI=no per ТЗ §10.1).",
      examples: ["REGEN", "RE", "РЕГЕН"],
      seeAlso: ["ZOOM", "PAN"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.regen.label",
    labelKeyEn: "command.regen.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: "RE",
  },
  // View operation — no geometry reducer; the handler is App.tsx-local and the
  // typed effect is a `view-op` audit event (recordViewOp), not a command.
  reducerLocations: [],
};

const REGENALL: CommandDescriptor = {
  id: "REGENALL",
  category: "system",
  status: "implemented",
  // ТЗ §10.1 — REGENALL is a display-refresh view-op (AI=no). The synchronous
  // handler + view-op telemetry exist (App.tsx REGENALL branch → recordViewOp).
  aiCallable: false,
  // EN: REGENALL + the acad.pgp `REA *REGENALL` alias. RU: CIS command name
  // РЕГЕНВСЕ (the forms the App.tsx handler already accepts).
  aliases: { en: ["REGENALL", "REA"], ru: ["РЕГЕНВСЕ"] },
  commandTypeLiterals: [],
  // REGENALL takes no arguments — it issues a display-refresh view-op across
  // every viewport / layout (the reactive renderer keeps geometry synced).
  parameters: [],
  help: {
    ru: {
      short: "Обновляет отображение всех видовых экранов (view-op РЕГЕНВСЕ).",
      long: "Команда РЕГЕНВСЕ (REGENALL / REA) — операция обновления отображения ВСЕХ видовых экранов и компоновок (листов) (display-refresh view-op, ТЗ §10.1), в отличие от РЕГЕН, затрагивающего только текущий видовой экран. Обработчик командной строки завершается синхронно и записывает ровно один телеметрический view-op `regenall` через `recordViewOp` (ТЗ §14). Как и РЕГЕН, в реактивном рендерере отображаемая геометрия непрерывно синхронизирована с моделью, поэтому отдельный пересчёт или перерисовка кэша этой командой не выполняются. Команда выполняется из командной строки / меню; жесты не задействованы. Геометрия не изменяется. Не вызывается ИИ (display-refresh — AI=no по ТЗ §10.1).",
      examples: ["REGENALL", "REA", "РЕГЕНВСЕ"],
      seeAlso: ["ZOOM", "PAN"],
    },
    en: {
      short: "Refreshes the display of all viewports (REGENALL view-op).",
      long: "The REGENALL command (REGENALL / REA) is a display-refresh view operation across ALL viewports and layouts (ТЗ §10.1), unlike REGEN, which covers only the current viewport. The command-line handler completes synchronously and records exactly one `regenall` view-op telemetry event via `recordViewOp` (ТЗ §14). As with REGEN, the reactive renderer keeps the displayed geometry continuously in sync with the model, so the command performs no separate cache recompute or redraw. It is invoked from the command line / menu; no gesture is involved. No geometry is modified. It is NOT AI-callable (a display-refresh is AI=no per ТЗ §10.1).",
      examples: ["REGENALL", "REA", "РЕГЕНВСЕ"],
      seeAlso: ["ZOOM", "PAN"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.regenall.label",
    labelKeyEn: "command.regenall.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: "REA",
  },
  // View operation — no geometry reducer; the handler is App.tsx-local and the
  // typed effect is a `view-op` audit event (recordViewOp), not a command.
  reducerLocations: [],
};

export const SYSTEM_DESCRIPTORS: readonly CommandDescriptor[] = [
  ANNOALLVISIBLE,
  ANNOAUTOSCALE,
  ANNOMONITOR,
  ANNORESET,
  ANNOUPDATE,
  BASE,
  BLOCKPROPERTIESMANAGER,
  CANNOSCALE,
  CHPROP,
  COLOR,
  GCCONSTRAINT,
  LAYER,
  MATCHPROP,
  OBJECTSCALE,
  PAN,
  PROPERTIES,
  PURGE,
  REGEN,
  REGENALL,
  RENAME,
  SCALELISTEDIT,
  ZOOM,
];
